import {z as zod} from "zod";
import {NULL_SENTINEL} from "~/components/ottopilotDataViewer/filterComponentStupider";
import {isCreatedUpdatedByFilterColumnId} from "~/utilities/deprecatedSystemGeneratedCreatedUpdatedByUserIds";
import {SYSTEM_USER_ID} from "@vesta/vos-types/generic/constants/SystemUserIds";
import {columnValuesFromApiZodValidator} from "~/utilities/columnValuesFromApi";
import {coerceFilterLineItemValueForApi} from "~/utilities/coerceDataViewerFilterValueForApi";
import {type FilterConfiguration, type FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import type {SearchConfiguration} from "~/components/ottopilotDataViewer/searchConfiguration";
import {SortDirection, type SortConfiguration} from "~/components/ottopilotDataViewer/sortConfiguration";
import type {OttopilotAnnouncement} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotAnnouncementsWorkspaceCapability/domain/OttopilotAnnouncement";
import {
    BtlActivityStatus,
    type BtlActivityChecklistResponsesByKey,
    btlActivityApiTypeArraySchema,
    buildBtlActivityUpdateApiData,
    type AssignmentMode,
    type BtlActivity,
    type BtlActivityApiType,
    type BtlActivityTemplates,
    type BtlReviewItemApiType,
    type BtlReviewStatus,
} from "~/domainModels/btlActivities";
import {btlActivityTemplateDataArrayApiResponseSchema, type BtlActivityTemplate, type BtlActivityTemplateData} from "~/domainModels/btlActivityTemplates";
import {
    OttopilotContestStatus,
    type OttopilotContest,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotContestsWorkspaceCapability/domain/OttopilotContest";
import {
    ottopilotEventCudLogValidator,
    ottopilotEventValidator,
    type OttopilotEvent,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEvent";
import {
    ottopilotEventContainerValidator,
    ottopilotEventContainerWithEventsValidator,
    type OttopilotEventContainer,
    type OttopilotEventContainerWithEvents,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import {LogActionType, type EventLog, type UserLog} from "~/domainModels/logs";
import type {ComplexFilter} from "@vesta/vos-types/modules/workspaceLevel/domain/ComplexFilter";
import {NotificationType, type Notification} from "@vesta/vos-types/modules/platformLevel/notifications/domain/Notification";
import type {TimeZone} from "@vesta/vos-types/modules/platformLevel/timeZones/domain/TimeZone";
import {UserStatus} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import {
    buildPrivateInviteSearchFilterGroup,
    type CreateJoinRequestRequest,
    type CreatePrivateInviteResponse,
    type GetPrivateInviteResponseData,
    type PrivateInviteSearchFilterGroup,
} from "~/domainModels/workspaceModules/inviteModule";
import {
    privateInviteVerificationRemarkValidator,
    type PrivateInviteChannels,
    PrivateInviteStatus,
    type PrivateInvite,
    type PrivateInviteVerificationRemark,
} from "@vesta/vos-types/modules/workspaceLevel/privateInvites/domain/PrivateInvite";
import {
    ottopilotCustomerApiRowSchema,
    mapOttopilotCustomerApiRowToOttopilotCustomer,
    type OttopilotCustomer,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotCustomersWorkspaceCapability/domain/OttopilotCustomer";
import {emotoradEnquiryAonConfig, emotoradJourneyAonConfig} from "~/domainModels/workspaceSpecific/emotorad";
import {buildAonCreatedAtOrFilterGroupFromRangesLineItems, partitionAonFilterLineItems, type AonWorkspaceConfig} from "~/utilities/syntheticFields/aon";
import type {Integer, Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {robustJsonResultServerFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultServerFetch";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import {
    integerZodValidator,
    iso8601DateTimeZodValidator,
    iso8601DateZodValidator,
    uuidZodValidator,
} from "@vesta/global-common-typescript/validators/zodValidators";
import {btlActivityTemplateDataApiResponseSchema} from "~/domainModels/btlActivityTemplates";

function getJourneyAonConfigs(): ReadonlyArray<AonWorkspaceConfig> {
    return [emotoradJourneyAonConfig];
}

function getEventAonConfigs(): ReadonlyArray<AonWorkspaceConfig> {
    return [emotoradJourneyAonConfig, emotoradEnquiryAonConfig];
}

export async function getEventContainers({
    workspaceId,
    offset,
    limit,
    filterConfiguration,
    sortConfiguration,
    searchConfiguration,
    templateId,
    // journeysModuleConfiguration,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    offset: number;
    limit: number;
    filterConfiguration: FilterConfiguration;
    sortConfiguration: SortConfiguration;
    searchConfiguration?: SearchConfiguration;
    templateId: Uuid;
    // journeysModuleConfiguration: JourneysModuleConfiguration;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Array<OttopilotEventContainer>>> {
    return await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/ottopilot/event-containers/get-all`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({
                templateId: templateId,
                offset: offset,
                limit: limit,
                filters: convertFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration),
                orderBy: convertSortConfigurationToSrimanOrderBy(sortConfiguration),
            }),
        },
        zod.array(ottopilotEventContainerValidator),
    );
}
export type EventContainerWithEvents = OttopilotEventContainerWithEvents;

const inFlightEventContainersWithEventsRequests = new Map<
    string,
    Promise<Result<Array<EventContainerWithEvents>>>
>();

export async function getEventContainersWithEvents({
    workspaceId,
    offset,
    limit,
    filterConfiguration,
    sortConfiguration,
    searchConfiguration,
    templateId,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    offset: number;
    limit: number;
    searchConfiguration?: SearchConfiguration;
    templateId: Uuid;
    filterConfiguration: FilterConfiguration;
    sortConfiguration: SortConfiguration;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Array<EventContainerWithEvents>>> {
    const requestUrl = `${intellsysBaseUrl}/sub-system/pluto/ottopilot/event-containers/events/get-all`;
    const requestBody = {
        templateId: templateId,
        offset: offset,
        limit: limit,
        filters: convertFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration),
        orderBy: convertSortConfigurationToSrimanOrderBy(sortConfiguration),
    };
    const requestKey = JSON.stringify({requestUrl, requestBody, workspaceId});
    const existingRequest = inFlightEventContainersWithEventsRequests.get(requestKey);
    if (existingRequest !== undefined) {
        return existingRequest;
    }

    const request = (async (): Promise<Result<Array<EventContainerWithEvents>>> => {
        const eventContainersResult = await robustJsonResultClientFetch(
            requestUrl,
            {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                    "X-Workspace-Id": workspaceId,
                },
                body: JSON.stringify(requestBody),
            },
            zod.array(ottopilotEventContainerWithEventsValidator),
        );
        return eventContainersResult;
    })();

    inFlightEventContainersWithEventsRequests.set(requestKey, request);
    request.then(
        () => inFlightEventContainersWithEventsRequests.delete(requestKey),
        () => inFlightEventContainersWithEventsRequests.delete(requestKey),
    );
    return request;
}

export async function getEventContainerColumnValues({
    workspaceId,
    columnId,
    intellsysBaseUrl,
    accessToken,
    templateId,
    filters,
}: {
    workspaceId: Uuid;
    columnId: string;
    intellsysBaseUrl: string;
    accessToken: string;
    /** Event-container template id (journey template). */
    templateId: Uuid;
    filters?: ComplexFilter;
}): Promise<Result<Array<string>>> {
    // return dataResult([
    //     "test",
    //     "test2",
    //     "test3",
    // ]);
    const eventContainerColumnValuesResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/ottopilot/event-containers/column-values`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({
                column: columnId,
                templateId: templateId,
                ...(filters === undefined ? {} : {filters: filters}),
            }),
        },
        columnValuesFromApiZodValidator,
    );
    return eventContainerColumnValuesResult;
}

export async function createCustomer({
    accessToken,
    intellsysBaseUrl,
    workspaceId,
    data,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
    data: Record<Uuid, unknown>;
}): Promise<Result<Uuid>> {
    return robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/ottopilot/customers/create`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                data: data,
            }),
        },
        uuidZodValidator,
    );
}

export async function getCustomers({
    workspaceId,
    offset,
    limit,
    filterConfiguration,
    sortConfiguration,
    searchConfiguration,
    columns,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    offset: number;
    limit: number;
    filterConfiguration: FilterConfiguration;
    sortConfiguration: SortConfiguration;
    searchConfiguration?: SearchConfiguration;
    columns?: Array<string>;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Array<OttopilotCustomer>>> {
    const customersResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/ottopilot/customers/get`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                offset: offset,
                limit: limit,
                filters: convertFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration),
                orderBy: convertSortConfigurationToSrimanOrderBy(sortConfiguration),
                ...(columns !== undefined ? {columns: columns} : {}),
            }),
        },
        zod.array(ottopilotCustomerApiRowSchema),
    );
    if (customersResult.success !== true) {
        return customersResult;
    }
    return dataResult(customersResult.data.map((row) => mapOttopilotCustomerApiRowToOttopilotCustomer(row)));
}

export async function getCustomers_ColumnValues({
    workspaceId,
    columnId,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    columnId: string;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Array<string>>> {
    return robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/ottopilot/customers/column-values`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                column: columnId,
            }),
        },
        columnValuesFromApiZodValidator,
    );
}

export async function getCustomers_Length({
    workspaceId,
    filterConfiguration,
    searchConfiguration,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    filterConfiguration: FilterConfiguration;
    searchConfiguration?: SearchConfiguration;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Integer>> {
    return robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/ottopilot/customers/count`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                filters: convertFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration),
            }),
        },
        integerZodValidator,
    );
}

export async function updateCustomer({
    accessToken,
    intellsysBaseUrl,
    customerId,
    workspaceId,
    data,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    customerId: Uuid;
    workspaceId: Uuid;
    data: Record<Uuid, unknown>;
}): Promise<Result<void>> {
    const updateResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/ottopilot/customers/update`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                id: customerId,
                workspaceId: workspaceId,
                data: data,
            }),
        },
        zod.unknown(),
    );
    if (updateResult.success !== true) {
        return updateResult;
    }
    return dataResult(undefined);
}

export async function getEvent_ColumnValues({
    workspaceId,
    columnId,
    intellsysBaseUrl,
    accessToken,
    templateId,
    filters,
}: {
    workspaceId: Uuid;
    columnId: string;
    intellsysBaseUrl: string;
    accessToken: string;
    templateId: Uuid;
    filters?: ComplexFilter;
}): Promise<Result<Array<string>>> {
    const eventColumnValuesResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/ottopilot/events/column-values`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({
                templateId: templateId,
                column: columnId,
                ...(filters === undefined ? {} : {filters: filters}),
            }),
        },
        columnValuesFromApiZodValidator,
    );
    return eventColumnValuesResult;
}

export async function getUesrs_ColumnValues({
    workspaceId,
    columnId,
    intellsysBaseUrl,
    accessToken,
    templateId,
}: {
    workspaceId: Uuid;
    columnId: string;
    intellsysBaseUrl: string;
    accessToken: string;
    templateId: Uuid;
}): Promise<Result<Array<string>>> {
    const usersColumnValuesResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/users/column-values`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({
                templateId: templateId,
                column: columnId,
            }),
        },
        columnValuesFromApiZodValidator,
    );
    return usersColumnValuesResult;
}

export async function getBtlActivity_ColumnValues({
    workspaceId,
    columnId,
    intellsysBaseUrl,
    accessToken,
    templateId,
}: {
    workspaceId: Uuid;
    columnId: string;
    intellsysBaseUrl: string;
    accessToken: string;
    templateId: Uuid;
}): Promise<Result<Array<string>>> {
    const eventColumnValuesResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/btl-activities/column-values`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                templateId: templateId,
                workspaceId: workspaceId,
                column: columnId,
            }),
        },
        columnValuesFromApiZodValidator,
    );
    return eventColumnValuesResult;
}

export async function getEvents({
    accessToken,
    intellsysBaseUrl,
    // eventTemplateId,
    // eventsModuleConfiguration,
    filterConfiguration,
    sortConfiguration,
    searchConfiguration,
    limit,
    offset,
    workspaceId,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    // eventTemplateId: Uuid;
    // eventsModuleConfiguration: EventsModuleConfiguration;
    filterConfiguration: FilterConfiguration;
    sortConfiguration: SortConfiguration;
    searchConfiguration?: SearchConfiguration;
    offset: number;
    limit: number;
    workspaceId: Uuid;
}): Promise<Result<Array<OttopilotEvent>>> {
    const eventsResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/ottopilot/events/get-all`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({
                // eventTemplateId: eventTemplateId,
                offset: offset,
                limit: limit,
                filters: convertFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration),
                orderBy: convertSortConfigurationToSrimanOrderBy(sortConfiguration),
            }),
        },
        zod.array(ottopilotEventValidator),
    );
    return eventsResult;
}

export type {EventWithEventContainer} from "~/domainModels/eventContainers";
import {eventWithEventContainerValidator, type EventWithEventContainer} from "~/domainModels/eventContainers";

export async function getEventsWithEventContainers({
    accessToken,
    intellsysBaseUrl,
    filterConfiguration,
    sortConfiguration,
    searchConfiguration,
    limit,
    offset,
    workspaceId,
    eventTemplateId,
    eventTemplateAdditionalColumnIds,
    eventContainerAdditionalColumnIds,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    filterConfiguration: FilterConfiguration;
    sortConfiguration: SortConfiguration;
    searchConfiguration?: SearchConfiguration;
    offset: number;
    limit: number;
    workspaceId: Uuid;
    eventTemplateId?: Uuid;
    eventTemplateAdditionalColumnIds?: Array<Uuid>;
    eventContainerAdditionalColumnIds?: Array<Uuid>;
}): Promise<Result<Array<EventWithEventContainer>>> {
    const filters =
        eventTemplateId !== undefined && eventTemplateAdditionalColumnIds !== undefined && eventContainerAdditionalColumnIds !== undefined
            ? convertFilterConfigurationToSrimanFiltersForEvents(
                  filterConfiguration,
                  eventTemplateAdditionalColumnIds,
                  eventContainerAdditionalColumnIds,
                  searchConfiguration,
              )
            : convertFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration);

    const eventsResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/ottopilot/events/event-containers/get-all`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({
                ...(eventTemplateId !== undefined ? {templateId: eventTemplateId} : {}),
                offset: offset,
                limit: limit,
                filters: filters,
                orderBy:
                    eventTemplateAdditionalColumnIds !== undefined && eventContainerAdditionalColumnIds !== undefined
                        ? convertSortConfigurationToSrimanOrderByForEvents(
                              sortConfiguration,
                              eventTemplateAdditionalColumnIds,
                              eventContainerAdditionalColumnIds,
                          )
                        : convertSortConfigurationToSrimanOrderBy(sortConfiguration),
            }),
        },
        zod.array(eventWithEventContainerValidator),
    );
    return eventsResult;
}

export async function getEvents_Length({
    accessToken,
    intellsysBaseUrl,
    filterConfiguration,
    workspaceId,
    eventTemplateId,
    eventTemplateAdditionalColumnIds,
    eventContainerAdditionalColumnIds,
    searchConfiguration,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    filterConfiguration: FilterConfiguration;
    searchConfiguration?: SearchConfiguration;
    workspaceId: Uuid;
    eventTemplateId?: Uuid;
    eventTemplateAdditionalColumnIds?: Array<Uuid>;
    eventContainerAdditionalColumnIds?: Array<Uuid>;
}): Promise<Result<Integer>> {
    const filters =
        eventTemplateId !== undefined && eventTemplateAdditionalColumnIds !== undefined && eventContainerAdditionalColumnIds !== undefined
            ? convertFilterConfigurationToSrimanFiltersForEvents(
                  filterConfiguration,
                  eventTemplateAdditionalColumnIds,
                  eventContainerAdditionalColumnIds,
                  searchConfiguration,
              )
            : convertFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration);

    const eventsLengthResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/ottopilot/events/event-containers/count-all`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({
                ...(eventTemplateId !== undefined ? {templateId: eventTemplateId} : {}),
                filters: filters,
            }),
        },
        integerZodValidator,
    );
    return eventsLengthResult;
}

export async function getEventLogs({
    workspaceId,
    offset,
    limit,
    filterConfiguration,
    searchConfiguration,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    offset: number;
    limit: number;
    filterConfiguration: FilterConfiguration;
    searchConfiguration?: SearchConfiguration;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Array<EventLog>>> {
    const eventsResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/ottopilot/events/cud/logs/get`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({
                offset: offset,
                limit: limit,
                filters: convertFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration),
            }),
        },
        zod.array(ottopilotEventCudLogValidator),
    );
    if (eventsResult.success !== true) {
        return eventsResult;
    }
    const eventsLogs = eventsResult.data;

    return dataResult(eventsLogs);
}

export async function getUserLogs({
    workspaceId,
    userId,
    offset,
    limit,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    userId: Uuid;
    offset: number;
    limit: number;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Array<UserLog>>> {
    const filterConfiguration: FilterConfiguration = {
        revisionId: generateUuid(),
        lineItems: [
            {
                columnId: "userId",
                operation: FilterOperation.equals,
                value: userId,
            },
        ],
    };
    const sortConfiguration: SortConfiguration = {
        revisionId: generateUuid(),
        lineItems: [
            {
                columnId: "timestamp",
                direction: SortDirection.descending,
            },
        ],
    };
    const userLogsResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/user-template-items/cud/logs/get`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                offset: offset,
                limit: limit,
                filters: convertFilterConfigurationToSrimanFilters(filterConfiguration, undefined),
                orderBy: convertSortConfigurationToSrimanOrderBy(sortConfiguration),
            }),
        },
        zod.array(
            zod.object({
                id: uuidZodValidator,
                actionType: zod.enum(LogActionType),
                actionDetails: zod.record(zod.string(), zod.unknown()),
                userId: uuidZodValidator,
                timestamp: iso8601DateTimeZodValidator,
            }),
        ),
    );
    if (userLogsResult.success !== true) {
        return userLogsResult;
    }
    return dataResult(userLogsResult.data);
}

// export async function getOrganizationalUnitTemplates({
//     workspaceId,
//     offset,
//     limit,
//     filterConfiguration,
//     intellsysBaseUrl,
//     accessToken,
// }: {
//     workspaceId: Uuid;
//     offset: number;
//     limit: number;
//     filterConfiguration: FilterConfiguration;
//     intellsysBaseUrl: string;
//     accessToken: string;
// }): Promise<Result<Array<OrganizationalUnitTemplate>>> {
//     const organizationalUnitTemplatesResult = await robustJsonResultClientFetch(
//         `${intellsysBaseUrl}/sub-system/pluto/api/organisational-units/get`,
//         {
//             method: "POST",
//             headers: {
//                 Authorization: `Bearer ${accessToken}`,
//                 "Content-Type": "application/json",
//             },
//             body: JSON.stringify({
//                 workspaceId: workspaceId,
//                 offset: offset,
//                 limit: limit,
//                 filters: [],
//             }),
//         },
//         zod.array(organizationalUnitTemplateZodValidator),
//     );
//     return organizationalUnitTemplatesResult;
// }

// export async function getOrganizationalUnitTemplates_Length({
//     workspaceId,
//     offset,
//     limit,
//     filterConfiguration,
//     intellsysBaseUrl,
//     accessToken,
// }: {
//     workspaceId: Uuid;
//     offset: number;
//     limit: number;
//     filterConfiguration: FilterConfiguration;
//     intellsysBaseUrl: string;
//     accessToken: string;
// }): Promise<Result<Integer>> {
//     const organizationalUnitTemplatesLengthResult = await robustJsonResultClientFetch(
//         `${intellsysBaseUrl}/sub-system/pluto/api/organisational-units/count`,
//         {
//             method: "POST",
//             headers: {
//                 Authorization: `Bearer ${accessToken}`,
//                 "Content-Type": "application/json",
//             },
//             body: JSON.stringify({
//                 workspaceId: workspaceId,
//                 filters: [],
//             }),
//         },
//         integerZodValidator,
//     );
//     return organizationalUnitTemplatesLengthResult;
// }

export async function updateOrganizationalUnit({
    workspaceId,
    templateId,
    id,
    data,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    templateId: Uuid;
    id: Uuid;
    data: Record<string, unknown>;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<void>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/organisational-unit-items/update`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                templateId: templateId,
                id: id,
                data: data,
            }),
        },
        zod.unknown().nullable(),
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(undefined);
}

export async function updateUser({
    workspaceId,
    templateId,
    id,
    data,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    templateId: Uuid;
    id: Uuid;
    data: Record<string, unknown>;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<void>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/user-template-items/update`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                templateId: templateId,
                id: id,
                data: data,
            }),
        },
        zod.unknown().nullable(),
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(undefined);
}

export async function updateUserSelf({
    workspaceId,
    templateId,
    data,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    templateId: Uuid;
    data: Record<string, unknown>;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<void>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/user-template-items/update-self`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                templateId: templateId,
                data: data,
            }),
        },
        zod.unknown().nullable(),
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(undefined);
}

export async function createJoinRequest({
    body,
    intellsysBaseUrl,
    accessToken,
}: {
    body: CreateJoinRequestRequest;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<void>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/join-requests/create`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
        },
        zod.unknown().nullable(),
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(undefined);
}

export async function uploadImage({
    file,
    workspaceId,
    intellsysBaseUrl,
    accessToken,
}: {
    file: File;
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<{fileUrl: string}>> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("workspaceId", workspaceId);

    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/upload/image`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: formData,
        },
        zod.object({fileUrl: zod.string()}),
    );
    return result;
}

export async function uploadAsset({
    file,
    workspaceId,
    intellsysBaseUrl,
    accessToken,
}: {
    file: File;
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<{id: Uuid}>> {
    const formData = new FormData();
    formData.append("file", file);

    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/assets`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "X-Workspace-Id": workspaceId,
            },
            body: formData,
        },
        zod.object({id: uuidZodValidator}),
    );
    return result;
}

export async function getAssetById({
    workspaceId,
    assetId,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    assetId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Blob>> {
    try {
        const response = await fetch(`${intellsysBaseUrl}/sub-system/pluto/assets/${assetId}`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "X-Workspace-Id": workspaceId,
            },
            cache: "no-store",
        });
        if (response.ok === true) {
            const blob = await response.blob();
            return dataResult(blob);
        }

        return errorResult(new Error(`Failed to fetch asset ${assetId}: ${response.status} ${response.statusText}`));
    } catch (exception) {
        return errorResult(exception instanceof Error ? exception : new Error(`Failed to fetch asset ${assetId}`));
    }
}

export async function getContests({
    workspaceId,
    offset,
    limit,
    filterConfiguration,
    sortConfiguration,
    searchConfiguration,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    offset: number;
    limit: number;
    filterConfiguration: FilterConfiguration;
    sortConfiguration: SortConfiguration;
    searchConfiguration?: SearchConfiguration;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Array<OttopilotContest>>> {
    const contestsResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/contests/get`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                offset: offset,
                limit: limit,
                filters: convertFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration),
                orderBy: convertSortConfigurationToSrimanOrderBy(sortConfiguration),
            }),
        },
        zod.array(
            zod.object({
                id: uuidZodValidator,
                createdBy: uuidZodValidator,
                createdAt: iso8601DateTimeZodValidator,
                updatedBy: uuidZodValidator,
                updatedAt: iso8601DateTimeZodValidator,
                status: zod.enum(OttopilotContestStatus),
                revisionId: uuidZodValidator,
                title: zod.string(),
                description: zod.string(),
                startDate: iso8601DateZodValidator,
                endDate: iso8601DateZodValidator,
                bannerImageUrl: zod.string(),
                termsAndConditions: zod.string(),
                validUserTemplateIds: zod.array(uuidZodValidator),
                // TODO: Validate using currencyIds
                currency: uuidZodValidator,
                currencyPerPoint: zod.number(),
                kpis: zod.array(
                    zod.object({
                        id: uuidZodValidator,
                        eventType: uuidZodValidator,
                        eventDisposition: uuidZodValidator,
                        points: zod.number(),
                        maxTimePerEvent: integerZodValidator,
                    }),
                ),
            }),
        ),
    );
    if (contestsResult.success !== true) {
        return contestsResult;
    }
    const contests = contestsResult.data;

    return dataResult(
        contests.map((contest): OttopilotContest => ({
            id: contest.id,
            createdBy: contest.createdBy,
            createdAt: contest.createdAt,
            updatedBy: contest.updatedBy,
            updatedAt: contest.updatedAt,
            status: contest.status,
            revisionId: contest.revisionId,
            title: contest.title,
            description: contest.description,
            startDate: contest.startDate,
            endDate: contest.endDate,
            bannerImageUrl: contest.bannerImageUrl,
            termsAndConditions: contest.termsAndConditions,
            eligibleUserTemplateIds: contest.validUserTemplateIds,
            payoutCurrencyId: contest.currency,
            payoutPointValue: contest.currencyPerPoint,
            kpis: contest.kpis.map((kpi) => ({
                id: kpi.id,
                eventTemplateId: kpi.eventType,
                dispositionId: kpi.eventDisposition,
                reward: kpi.points,
                limit: kpi.maxTimePerEvent,
            })),
        })),
    );
}

export async function getContests_Length({
    workspaceId,
    filterConfiguration,
    searchConfiguration,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    filterConfiguration: FilterConfiguration;
    searchConfiguration?: SearchConfiguration;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Integer>> {
    const contestsLengthResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/contests/count`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                filters: convertFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration),
            }),
        },
        integerZodValidator,
    );
    return contestsLengthResult;
}

export async function getAnnouncements({
    workspaceId,
    offset,
    limit,
    filterConfiguration,
    sortConfiguration,
    searchConfiguration,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    offset: number;
    limit: number;
    filterConfiguration: FilterConfiguration;
    sortConfiguration: SortConfiguration;
    searchConfiguration?: SearchConfiguration;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Array<OttopilotAnnouncement>>> {
    const announcementsResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/announcements/get`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                offset: offset,
                limit: limit,
                filters: convertFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration),
                orderBy: convertSortConfigurationToSrimanOrderBy(sortConfiguration),
            }),
        },
        zod.array(zod.unknown()),
    );
    if (announcementsResult.success !== true) {
        return announcementsResult;
    }
    const announcements = announcementsResult.data;

    return dataResult(announcements);
}

export async function getAnnouncements_Length({
    workspaceId,
    filterConfiguration,
    searchConfiguration,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    filterConfiguration: FilterConfiguration;
    searchConfiguration?: SearchConfiguration;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Integer>> {
    const announcementsLengthResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/announcements/count`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                filters: convertFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration),
            }),
        },
        integerZodValidator,
    );
    return announcementsLengthResult;
}

export async function getNotifications({
    workspaceId,
    offset,
    limit,
    filterConfiguration,
    sortConfiguration,
    searchConfiguration,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    offset: number;
    limit: number;
    filterConfiguration?: FilterConfiguration;
    sortConfiguration?: SortConfiguration;
    searchConfiguration?: SearchConfiguration;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Array<Notification>>> {
    const notificationsResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/notifications/get`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                offset: offset,
                limit: limit,
                filters: convertFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration),
                orderBy: convertSortConfigurationToSrimanOrderBy(sortConfiguration),
            }),
        },
        zod.array(zod.unknown()),
    );
    if (notificationsResult.success !== true) {
        return notificationsResult;
    }
    const notifications = notificationsResult.data;

    return dataResult(notifications);
}

export async function createNotification({
    workspaceId,
    userId,
    content,
    header,
    status,
    eventContainerId,
    notificationType,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    userId: Uuid;
    content: string;
    header?: string;
    status?: Uuid;
    eventContainerId?: Uuid;
    notificationType?: string;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Uuid>> {
    const notificationData: Record<string, unknown> = {
        userId,
    };

    if (eventContainerId !== undefined) {
        notificationData.eventContainerId = eventContainerId;
        notificationData.additionalColumnValues = {
            eventContainerId,
        };
    }

    const notificationResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/notifications/create`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                userId: userId,
                content: content,
                header: header,
                type: notificationType ?? NotificationType.eventContainer,
                status: status ?? ("28d0affb-5938-4828-a464-4c5789059ce5" as Uuid),
                data: notificationData,
            }),
        },
        uuidZodValidator,
    );

    if (notificationResult.success !== true) {
        return notificationResult;
    }

    return dataResult(notificationResult.data);
}

export async function createPrivateInvite({
    workspaceId,
    userTemplateId,
    expiresAt,
    status,
    allowedEmail,
    allowedPhoneNumber,
    inviteChannels,
    fixedAdditionalColumnValues,
    inviteFormData,
    parentUserId,
    verificationRemark,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    userTemplateId: Uuid;
    expiresAt: string;
    status: Uuid;
    allowedEmail: string;
    allowedPhoneNumber: string;
    inviteChannels: PrivateInviteChannels;
    fixedAdditionalColumnValues: Record<Uuid, unknown>;
    inviteFormData: Record<Uuid, unknown> | null;
    parentUserId: Uuid | null;
    verificationRemark: PrivateInviteVerificationRemark | null;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<CreatePrivateInviteResponse>> {
    const createPrivateInviteResponseSchema = zod.object({
        inviteUrl: zod.string(),
    });
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/private-invites/create`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId,
                userTemplateId,
                expiresAt,
                status,
                allowedEmail,
                allowedPhoneNumber,
                inviteChannels,
                fixedAdditionalColumnValues,
                inviteFormData,
                parentUserId,
                verificationRemark,
            }),
        },
        createPrivateInviteResponseSchema,
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(result.data);
}

export async function updatePrivateInvite({
    workspaceId,
    id,
    status,
    userTemplateId,
    fixedAdditionalColumnValues,
    inviteFormData,
    parentUserId,
    verificationRemark,
    allowedEmail,
    allowedPhoneNumber,
    expiresAt,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    id: Uuid;
    status: Uuid;
    userTemplateId?: Uuid;
    fixedAdditionalColumnValues?: Record<Uuid, unknown>;
    inviteFormData?: Record<Uuid, unknown> | null;
    parentUserId?: Uuid | null;
    verificationRemark?: PrivateInviteVerificationRemark | null;
    allowedEmail?: string;
    allowedPhoneNumber?: string;
    expiresAt?: string;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<void>> {
    const body: Record<string, unknown> = {
        workspaceId: workspaceId,
        id: id,
        status: status,
    };
    if (userTemplateId !== undefined) {
        body.userTemplateId = userTemplateId;
    }
    if (fixedAdditionalColumnValues !== undefined) {
        body.fixedAdditionalColumnValues = fixedAdditionalColumnValues;
    }
    if (inviteFormData !== undefined) {
        body.inviteFormData = inviteFormData;
    }
    if (parentUserId !== undefined) {
        body.parentUserId = parentUserId;
    }
    if (verificationRemark !== undefined) {
        body.verificationRemark = verificationRemark;
    }
    if (allowedEmail !== undefined) {
        body.allowedEmail = allowedEmail;
    }
    if (allowedPhoneNumber !== undefined) {
        body.allowedPhoneNumber = allowedPhoneNumber;
    }
    if (expiresAt !== undefined) {
        body.expiresAt = expiresAt;
    }
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/private-invites/update`,
        {
            method: "PUT",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
        },
        zod.unknown(),
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(undefined);
}

const privateInviteApiDataSchema = zod.object({
    id: uuidZodValidator,
    userTemplateId: uuidZodValidator,
    expiresAt: iso8601DateTimeZodValidator.nullable(),
    status: zod.enum(PrivateInviteStatus),
    allowedEmail: zod.string().trim().min(1),
    allowedPhoneNumber: zod.string().trim().min(1),
    fixedAdditionalColumnValues: zod.record(uuidZodValidator, zod.unknown()),
    inviteFormData: zod.record(uuidZodValidator, zod.unknown()).nullable(),
    inviteCode: zod.string(),
    parentUserId: uuidZodValidator.nullable(),
    createdAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    updatedBy: uuidZodValidator,
    verificationRemark: privateInviteVerificationRemarkValidator.nullable(),
    workspaceId: uuidZodValidator,
    submittedAt: iso8601DateTimeZodValidator.nullable(),
    submittedBy: uuidZodValidator.nullable(),
});

const getPrivateInviteUserTemplateItemSchema = zod.object({
    id: uuidZodValidator,
    userId: uuidZodValidator,
    templateId: uuidZodValidator,
    parentId: uuidZodValidator.nullable(),
    additionalColumnValues: zod.record(zod.string(), zod.unknown()),
    createdAt: zod.string(),
    createdBy: uuidZodValidator,
    updatedAt: zod.string(),
    updatedBy: uuidZodValidator,
    status: zod.enum(UserStatus),
});

const getPrivateInviteOrganisationalUnitItemSchema = zod.object({
    id: uuidZodValidator,
    templateId: uuidZodValidator,
    parentId: uuidZodValidator.nullable(),
    additionalColumnValues: zod.record(zod.string(), zod.unknown()),
    createdAt: zod.string(),
    createdBy: uuidZodValidator,
    updatedAt: zod.string(),
    updatedBy: uuidZodValidator,
    status: uuidZodValidator,
});

const getPrivateInviteResponseDataSchema = privateInviteApiDataSchema.extend({
    userTemplateItems: zod.array(getPrivateInviteUserTemplateItemSchema),
    organisationalUnitItems: zod.array(getPrivateInviteOrganisationalUnitItemSchema),
});

export async function getPrivateInvite({
    workspaceId,
    id,
    inviteCode,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    id: Uuid;
    inviteCode: string;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<GetPrivateInviteResponseData>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/private-invites/get`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                id: id,
                inviteCode: inviteCode,
            }),
        },
        getPrivateInviteResponseDataSchema,
    );
    if (result.success !== true) {
        return result;
    }
    const responseData = result.data;
    return dataResult(responseData);
}

function convertPrivateInviteFilterConfigurationToSrimanFilters(
    filterConfiguration: FilterConfiguration,
    searchConfiguration?: SearchConfiguration,
): FilterGroup {
    const baseFilterGroup = convertFilterConfigurationToSrimanFilters(filterConfiguration, undefined);
    const searchFilterGroup: PrivateInviteSearchFilterGroup | null = buildPrivateInviteSearchFilterGroup(searchConfiguration);
    if (searchFilterGroup === null) {
        return baseFilterGroup;
    }
    return {
        logic: "AND",
        filters: [...baseFilterGroup.filters, searchFilterGroup],
    };
}

export async function getPrivateInvites({
    workspaceId,
    offset,
    limit,
    filterConfiguration,
    sortConfiguration,
    searchConfiguration,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    offset: number;
    limit: number;
    filterConfiguration: FilterConfiguration;
    sortConfiguration: SortConfiguration;
    searchConfiguration?: SearchConfiguration;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Array<PrivateInvite>>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/private-invites/get-all`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                offset: offset,
                limit: limit,
                filters: convertPrivateInviteFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration),
                // orderBy: convertSortConfigurationToSrimanOrderBy(sortConfiguration),
            }),
        },
        zod.array(privateInviteApiDataSchema),
    );
    if (result.success !== true) {
        return result;
    }
    return result;
}

export async function getPrivateInvitesCount({
    workspaceId,
    filterConfiguration,
    searchConfiguration,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    filterConfiguration: FilterConfiguration;
    searchConfiguration?: SearchConfiguration;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Integer>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/private-invites/entries-count`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                filters: convertPrivateInviteFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration),
            }),
        },
        integerZodValidator,
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(result.data);
}

export async function getPrivateInvites_ColumnValues({
    workspaceId,
    columnId,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    columnId: string;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Array<string>>> {
    const privateInvitesColumnValuesResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/private-invites/column-values`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                column: columnId,
            }),
        },
        columnValuesFromApiZodValidator,
    );
    return privateInvitesColumnValuesResult;
}

export async function updateNotificationStatus({
    notificationId,
    statusId,
    workspaceId,
    intellsysBaseUrl,
    accessToken,
}: {
    notificationId: Uuid;
    statusId: Uuid;
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<void>> {
    const updateResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/notifications/update`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                id: notificationId,
                data: {
                    status: statusId,
                },
            }),
        },
        zod.unknown(),
    );

    if (updateResult.success !== true) {
        return updateResult;
    }

    return dataResult(undefined);
}

export async function createEvent({
    accessToken,
    intellsysBaseUrl,
    event,
    // eventsModuleConfiguration,
    workspaceId,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    event: Omit<OttopilotEvent, "id" | "revisionId" | "createdBy" | "createdAt" | "updatedBy" | "updatedAt" | "status">;
    // eventsModuleConfiguration: EventsModuleConfiguration;
    workspaceId: Uuid;
}): Promise<Result<Uuid>> {
    const eventIdResult = await robustJsonResultServerFetch(
        `${intellsysBaseUrl}/sub-system/pluto/ottopilot/events/create`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({
                templateId: event.templateId,
                data: {
                    event_container_id: event.eventContainerId,
                    ...event.additionalColumnValues,
                },
            }),
        },
        uuidZodValidator,
    );
    if (eventIdResult.success !== true) {
        return eventIdResult;
    }
    const eventId = eventIdResult.data;

    return dataResult(eventId);
}

export async function updateEvent({
    accessToken,
    intellsysBaseUrl,
    eventId,
    templateId,
    data,
    workspaceId,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    eventId: Uuid;
    templateId: Uuid;
    data: never;
    workspaceId: Uuid;
}): Promise<Result<void>> {
    const updateEventResult = await robustJsonResultServerFetch(
        `${intellsysBaseUrl}/sub-system/pluto/ottopilot/events/update`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({
                id: eventId,
                templateId: templateId,
                data: data,
            }),
        },
        zod.unknown(),
    );
    if (updateEventResult.success !== true) {
        return updateEventResult;
    }

    return dataResult(undefined);
}

export async function createContest({
    accessToken,
    intellsysBaseUrl,
    contest,
    workspaceId,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    contest: Omit<OttopilotContest, "id" | "createdBy" | "createdAt" | "updatedBy" | "updatedAt" | "status">;
    workspaceId: Uuid;
}): Promise<Result<Uuid>> {
    return await robustJsonResultServerFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/contests/create`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                data: {
                    status: OttopilotContestStatus.active,
                    revision_id: contest.revisionId,
                    title: contest.title,
                    description: contest.description,
                    start_date: contest.startDate,
                    end_date: contest.endDate,
                    banner_image_url: contest.bannerImageUrl,
                    terms_and_conditions: contest.termsAndConditions,
                    valid_user_template_ids: contest.eligibleUserTemplateIds,
                    currency: contest.payoutCurrencyId,
                    currency_per_point: contest.payoutPointValue,
                    kpis: contest.kpis.map((kpi) => ({
                        id: kpi.id,
                        event_type: kpi.eventTemplateId,
                        event_disposition: kpi.dispositionId,
                        max_time_per_event: kpi.limit,
                        points: kpi.reward,
                    })),
                    // estimatedBudget: number,
                    // proposedBudget: contest.proposedBudget,
                },
            }),
        },
        uuidZodValidator,
    );
}

// BTL Events API
export async function getBtlActivities({
    workspaceId,
    offset,
    limit,
    filterConfiguration,
    sortConfiguration,
    searchConfiguration,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    offset: number;
    limit: number;
    filterConfiguration: FilterConfiguration;
    sortConfiguration: SortConfiguration;
    searchConfiguration?: SearchConfiguration;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Array<BtlActivityApiType>>> {
    const btlEventsResult = await robustJsonResultClientFetch(
        // TODO: change endpoint
        `${intellsysBaseUrl}/sub-system/pluto/api/btl-activities/get-all`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                offset: offset,
                limit: limit,
                filters: convertFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration),
                orderBy: convertSortConfigurationToSrimanOrderBy(sortConfiguration),
            }),
        },
        btlActivityApiTypeArraySchema,
    );
    if (btlEventsResult.success !== true) {
        return btlEventsResult;
    }
    const btlEvents = btlEventsResult.data;

    return dataResult(btlEvents);
}

export async function getBtlActivities_Length({
    workspaceId,
    filterConfiguration,
    searchConfiguration,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    filterConfiguration: FilterConfiguration;
    searchConfiguration?: SearchConfiguration;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Integer>> {
    const btlEventsLengthResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/btl-activities/count-all`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                filters: convertFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration),
            }),
        },
        integerZodValidator,
    );
    return btlEventsLengthResult;
}

// #region BTL Activity Templates API
export async function getBtlActivityTemplates({
    workspaceId,
    offset,
    limit,
    filterConfiguration,
    sortConfiguration,
    searchConfiguration,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    offset: number;
    limit: number;
    filterConfiguration: FilterConfiguration;
    sortConfiguration: SortConfiguration;
    searchConfiguration?: SearchConfiguration;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Array<BtlActivityTemplateData>>> {
    const btlActivityTemplatesResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/btl-activity-templates/get`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                offset: offset,
                limit: limit,
                filters: convertFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration),
                orderBy: convertSortConfigurationToSrimanOrderBy(sortConfiguration),
            }),
        },
        btlActivityTemplateDataArrayApiResponseSchema,
    );
    if (btlActivityTemplatesResult.success !== true) {
        return btlActivityTemplatesResult;
    }
    return dataResult(btlActivityTemplatesResult.data);
}

export async function getBtlActivityTemplatesById({
    workspaceId,
    templateId,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    templateId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<BtlActivityTemplateData>> {
    const btlActivityTemplatesResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/btl-activity-templates/get-by-id`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                id: templateId,
            }),
        },
        btlActivityTemplateDataApiResponseSchema,
    );
    if (btlActivityTemplatesResult.success !== true) {
        return btlActivityTemplatesResult;
    }
    return dataResult(btlActivityTemplatesResult.data);
}

export async function createBtlActivityTemplate({
    workspaceId,
    payload,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    payload: BtlActivityTemplate;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Uuid>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/btl-activity-templates/create`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                id: payload.id,
                displayName: payload.title,
                description: payload.description,
                body: payload.body,
                checklists: payload.checklists,
                additionalColumns: [], // required per spec
            }),
        },
        uuidZodValidator,
    );

    return result;
}

export async function updateBtlActivityTemplate({
    workspaceId,
    payload,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    payload: BtlActivityTemplate;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Uuid>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/btl-activity-templates/update`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                id: payload.id,
                displayName: payload.title,
                description: payload.description,
                body: payload.body,
                checklists: payload.checklists,
            }),
        },
        uuidZodValidator,
    );

    return result;
}

export async function getBtlActivityTemplates_Length({
    workspaceId,
    filterConfiguration,
    searchConfiguration,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    filterConfiguration: FilterConfiguration;
    searchConfiguration?: SearchConfiguration;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Integer>> {
    const btlActivityTemplatesLengthResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/btl-activity-templates/entries-count`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                filters: convertFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration),
            }),
        },
        integerZodValidator,
    );
    return btlActivityTemplatesLengthResult;
}

export async function getBtlActivityTemplate_ColumnValues({
    workspaceId,
    columnId,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    columnId: string;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Array<string>>> {
    const columnValuesResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/btl-activity-templates/column-values`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                columnName: columnId,
            }),
        },
        zod.array(zod.string()),
    );
    return columnValuesResult;
}
// #endregion

export async function getBtlReviewItems({
    reviewId,
    offset,
    limit,
    filterConfiguration,
    sortConfiguration,
    searchConfiguration,
    intellsysBaseUrl,
    accessToken,
}: {
    reviewId: Uuid;
    offset: number;
    limit: number;
    filterConfiguration: FilterConfiguration;
    sortConfiguration: SortConfiguration;
    searchConfiguration?: SearchConfiguration;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Array<BtlReviewItemApiType>>> {
    const reviewItemsResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/btl-activities/review/get`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                reviewId: reviewId,
                offset: offset,
                limit: limit,
                filters: convertFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration),
                orderBy: convertSortConfigurationToSrimanOrderBy(sortConfiguration),
            }),
        },
        zod.array(
            zod.object({
                id: uuidZodValidator,
                title: zod.string(),
                btlActivityTemplateId: uuidZodValidator,
                validationStatus: zod.enum(BtlReviewStatus),
                validationErrors: zod.string(),
                assignedUsers: zod.array(uuidZodValidator),
                isAlldayEvent: zod.boolean(),
                startDate: iso8601DateTimeZodValidator,
                startTime: zod.string().optional(),
                endDate: iso8601DateTimeZodValidator,
                endTime: zod.string().optional(),
                region: zod.string().optional(),
                atoll: zod.string().optional(),
                island: zod.string().optional(),
            }),
        ),
    );
    if (reviewItemsResult.success !== true) {
        return reviewItemsResult;
    }
    const reviewItems = reviewItemsResult.data;

    return dataResult(
        reviewItems.map((item): BtlReviewItemApiType => ({
            id: item.id,
            title: item.title,
            btlActivityTemplateId: item.btlActivityTemplateId as BtlActivityTemplates,
            validationStatus: item.validationStatus,
            validationErrors: item.validationErrors,
            assignedUsers: item.assignedUsers,
            isAlldayEvent: item.isAlldayEvent,
            startDate: item.startDate,
            startTime: item.startTime,
            endDate: item.endDate,
            endTime: item.endTime,
            region: item.region,
            atoll: item.atoll,
            island: item.island,
        })),
    );
}

export async function getBtlReviewItems_Length({
    reviewId,
    filterConfiguration,
    searchConfiguration,
    intellsysBaseUrl,
    accessToken,
}: {
    reviewId: Uuid;
    filterConfiguration: FilterConfiguration;
    searchConfiguration?: SearchConfiguration;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Integer>> {
    const reviewItemsLengthResult = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/btl-activities/review/count`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                reviewId: reviewId,
                filters: convertFilterConfigurationToSrimanFilters(filterConfiguration, searchConfiguration),
            }),
        },
        integerZodValidator,
    );
    return reviewItemsLengthResult;
}

export async function createBtlActivity({
    payload,
    intellsysBaseUrl,
    accessToken,
}: {
    payload: {
        workspaceId: Uuid;
        templateId: Uuid;
        data: {
            btl_status: string;
            end_date: Iso8601DateTime;
            timezone: Uuid;
            description: string;
            geography_nodes: Array<Uuid>;
            title: string;
            assigned_users: Array<Uuid>;
            event_owner: Uuid | null;
            execution_role: Uuid | null;
            is_allday_event: boolean;
            assignment_mode: AssignmentMode;
            start_date: Iso8601DateTime;
            checklist_completion_details: unknown;
            gallery: Array<unknown>;
        };
    };
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<{id: Uuid}>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/btl-activities/create`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        },
        uuidZodValidator,
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult({id: result.data});
}

export async function updateBtlActivity({
    payload,
    intellsysBaseUrl,
    accessToken,
}: {
    payload: {
        workspaceId: Uuid;
        id: Uuid;
        templateId: Uuid;
        data: {
            btl_status?: string;
            end_date?: string;
            timezone?: string;
            description?: string;
            geography_nodes?: Array<Uuid>;
            title?: string;
            assigned_users?: Array<Uuid>;
            execution_role?: Uuid | null;
            is_allday_event?: boolean;
            assignment_mode?: string;
            start_date?: string;
            /** OttopilotEvent media metadata (asset uploads); shape should match backend contract. */
            gallery?: Array<{
                asset_id: string;
                file_name: string;
                uploaded_at: Iso8601DateTime;
            }>;
            /** Checklist submission data */
            checklist_completion_details?: BtlActivityChecklistResponsesByKey;
        };
    };
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<unknown>> {
    return await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/btl-activities/update`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        },
        zod.unknown(),
    );
}

/** Marks a BTL activity as Live via the standard update contract (full `data` payload, `btl_status` active). */
export async function startBtlActivity({
    workspaceId,
    id,
    templateId,
    row,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    id: Uuid;
    templateId: Uuid;
    row: BtlActivity;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<unknown>> {
    const data = buildBtlActivityUpdateApiData(row, row.gallery);
    return updateBtlActivity({
        payload: {
            workspaceId,
            id,
            templateId,
            data: {
                ...data,
                btl_status: BtlActivityStatus.active,
            },
        },
        intellsysBaseUrl,
        accessToken,
    });
}

/** Marks a BTL activity as cancelled via the standard update contract (full `data` payload, `btl_status` cancelled). */
export async function cancelBtlActivity({
    workspaceId,
    id,
    templateId,
    row,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    id: Uuid;
    templateId: Uuid;
    row: BtlActivity;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<unknown>> {
    const data = buildBtlActivityUpdateApiData(row, row.gallery);
    return updateBtlActivity({
        payload: {
            workspaceId,
            id,
            templateId,
            data: {
                ...data,
                btl_status: BtlActivityStatus.cancelled,
            },
        },
        intellsysBaseUrl,
        accessToken,
    });
}

/** Specific helper to persist just the checklist responses for a BTL activity. */
export async function updateChecklistResponses({
    workspaceId,
    id,
    templateId,
    checklistResponses,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    id: Uuid;
    templateId: Uuid;
    checklistResponses: Record<
        string,
        Array<{
            id: Uuid;
            userId: Uuid;
            textInput: string;
            createdAt: Iso8601DateTime;
            media: Array<{assetId: Uuid; fileName: string; uploadedAt: Iso8601DateTime}>;
        }>
    >;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<unknown>> {
    return updateBtlActivity({
        payload: {
            workspaceId,
            id,
            templateId,
            data: {
                checklist_completion_details: checklistResponses,
            },
        },
        intellsysBaseUrl,
        accessToken,
    });
}

export type CreateTargetPayload = {
    workspaceId: Uuid;
    usersScoreDistribution: Array<{
        id: Uuid;
        targetValue: number;
        eventType: string;
        date: string;
        productAllocations?: Array<{productId?: Uuid; productCatalogueId?: Uuid; targetValue: number}>;
    }>;
    lifecycleStages: Array<{eventType: Uuid; targetValue: number}>;
    metricMode: Uuid;
    productId: Uuid | null;
    startTime: string;
    dateTime: string;
};

const createTargetPayloadSchema = zod.object({
    workspaceId: uuidZodValidator,
    usersScoreDistribution: zod.array(
        zod.object({
            id: uuidZodValidator,
            targetValue: zod.number(),
            eventType: zod.string(),
            date: zod.string(),
            productAllocations: zod
                .array(
                    zod
                        .object({
                            productId: uuidZodValidator.optional(),
                            productCatalogueId: uuidZodValidator.optional(),
                            targetValue: zod.number(),
                        })
                        .refine((allocation) => allocation.productId !== undefined || allocation.productCatalogueId !== undefined, {
                            message: "productAllocations[].productId or productAllocations[].productCatalogueId is required",
                        }),
                )
                .optional(),
        }),
    ),
    lifecycleStages: zod.array(
        zod.object({
            eventType: uuidZodValidator,
            targetValue: zod.number(),
        }),
    ),
    metricMode: uuidZodValidator,
    productId: uuidZodValidator.nullable(),
    startTime: zod.string(),
    dateTime: zod.string(),
});

export async function createTarget({
    payload,
    intellsysBaseUrl,
    accessToken,
}: {
    payload: CreateTargetPayload;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<{id: Uuid}>> {
    const parsed = createTargetPayloadSchema.safeParse(payload);
    if (!parsed.success) {
        return errorResult(new Error(parsed.error.message));
    }
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/targets/create`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(parsed.data),
        },
        uuidZodValidator,
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult({id: result.data});
}

export type MapBtlActivityToTargetPayload = {
    workspaceId: Uuid;
    btlActivityTemplateId: Uuid;
    btlActivityId: Uuid;
    targetId: Uuid;
};

const mapBtlActivityToTargetPayloadSchema = zod.object({
    workspaceId: uuidZodValidator,
    btlActivityTemplateId: uuidZodValidator,
    btlActivityId: uuidZodValidator,
    targetId: uuidZodValidator,
});

export async function mapBtlActivityToTargetId({
    payload,
    intellsysBaseUrl,
    accessToken,
}: {
    payload: MapBtlActivityToTargetPayload;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<void>> {
    const parsed = mapBtlActivityToTargetPayloadSchema.safeParse(payload);
    if (!parsed.success) {
        return errorResult(new Error(parsed.error.message));
    }
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/btl-activity-targets/create`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(parsed.data),
        },
        zod.unknown(),
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(undefined);
}

export type BtlActivityTargetMappingRecord = {
    id?: Uuid;
    workspaceId?: Uuid;
    btlActivityTemplateId?: Uuid;
    btlActivityId?: Uuid;
    targetId?: Uuid;
    createdAt?: string;
    updatedAt?: string;
    createdBy?: Uuid;
    updatedBy?: Uuid;
};

export type BtlActivityTargetMappingsGetResponsePayload =
    Array<BtlActivityTargetMappingRecord> | BtlActivityTargetMappingRecord | {data: Array<BtlActivityTargetMappingRecord> | BtlActivityTargetMappingRecord};

export async function listBtlActivityTargetMappingsViaGet({
    workspaceId,
    id,
    btlActivityTemplateId,
    btlActivityId,
    targetId,
    offset,
    limit,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    id: Uuid | null;
    btlActivityTemplateId: Uuid | null;
    btlActivityId: Uuid | null;
    targetId: Uuid | null;
    offset: number;
    limit: number;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<BtlActivityTargetMappingsGetResponsePayload>> {
    return await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/btl-activity-targets/get`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId,
                id,
                btlActivityTemplateId,
                btlActivityId,
                targetId,
                offset,
                limit,
            }),
        },
        zod.custom<BtlActivityTargetMappingsGetResponsePayload>(() => true),
    );
}

export function parseBtlActivityTargetMappingsGetResponsePayload(
    payload: BtlActivityTargetMappingsGetResponsePayload | null | undefined,
): Array<BtlActivityTargetMappingRecord> {
    if (payload === null || payload === undefined) {
        return [];
    }
    if (Array.isArray(payload)) {
        return payload.filter((item): item is BtlActivityTargetMappingRecord => item !== null && typeof item === "object");
    }
    if (typeof payload === "object") {
        const record = payload as BtlActivityTargetMappingRecord | {data: Array<BtlActivityTargetMappingRecord> | BtlActivityTargetMappingRecord};
        if ("data" in record && Array.isArray(record.data)) {
            return parseBtlActivityTargetMappingsGetResponsePayload(record.data);
        }
        if ("data" in record && record.data !== undefined && record.data !== null && typeof record.data === "object") {
            return [record.data as BtlActivityTargetMappingRecord];
        }
        if ("targetId" in record || "btlActivityId" in record || "btlActivityTemplateId" in record) {
            return [record as BtlActivityTargetMappingRecord];
        }
    }
    return [];
}

export type TargetGetProductAllocation = {
    productId?: Uuid;
    productCatalogueId?: Uuid;
    targetValue: number;
};

export type TargetGetUserScoreDistribution = {
    id: Uuid;
    targetValue: number;
    eventType: Uuid | string;
    date: string;
    productAllocations?: Array<TargetGetProductAllocation>;
};

export type TargetGetLifecycleStage = {
    eventType: Uuid;
    targetValue: number;
};

export type TargetGetRecord = {
    id?: Uuid;
    workspaceId?: Uuid;
    usersScoreDistribution?: Array<TargetGetUserScoreDistribution>;
    lifecycleStages?: Array<TargetGetLifecycleStage>;
    metricMode?: Uuid;
    productId?: Uuid | null;
    startTime?: string;
    dateTime?: string;
    createdAt?: string;
    updatedAt?: string;
    createdBy?: Uuid;
    updatedBy?: Uuid;
};

export type TargetsGetResponsePayload = Array<TargetGetRecord> | TargetGetRecord | {data: Array<TargetGetRecord> | TargetGetRecord};

/**
 * `POST /api/targets/get` — when `id` is `null`, returns a paginated array of full target
 * documents (`lifecycleStages`, `usersScoreDistribution`, …). When `id` is set, behaviour
 * depends on backend (may still return an array).
 */
export async function listTargetsViaTargetsGet({
    workspaceId,
    id,
    offset,
    limit,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    id: Uuid | null;
    offset: number;
    limit: number;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<TargetsGetResponsePayload>> {
    return await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/targets/get`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId,
                id,
                offset,
                limit,
            }),
        },
        zod.custom<TargetsGetResponsePayload>(() => true),
    );
}

/** Normalizes the `data` field from `/api/targets/get` into target-shaped records. */
export function parseTargetsGetResponsePayload(payload: TargetsGetResponsePayload | null | undefined): Array<TargetGetRecord> {
    if (payload === null || payload === undefined) {
        return [];
    }
    if (Array.isArray(payload)) {
        return payload.filter((item): item is TargetGetRecord => item !== null && typeof item === "object");
    }
    if (typeof payload === "object") {
        const record = payload as TargetGetRecord | {data: Array<TargetGetRecord> | TargetGetRecord};
        if ("data" in record && Array.isArray(record.data)) {
            return parseTargetsGetResponsePayload(record.data);
        }
        if ("data" in record && record.data !== undefined && record.data !== null && typeof record.data === "object") {
            return parseTargetsGetResponsePayload(record.data as TargetGetRecord);
        }
        if ("lifecycleStages" in record || "usersScoreDistribution" in record || "lifecycle_stages" in record || "users_score_distribution" in record) {
            return [record as TargetGetRecord];
        }
    }
    return [];
}

/**
 * Fetches a single target. Tries `/api/targets/get` with id + pagination fields first,
 * then legacy `get-by-id` routes.
 */
export async function getTargetForWorkspace({
    workspaceId,
    targetId,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    targetId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<TargetGetRecord>> {
    const headers = {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json" as const,
    };

    const primary = await listTargetsViaTargetsGet({
        workspaceId,
        id: targetId,
        offset: 0,
        limit: 50,
        intellsysBaseUrl,
        accessToken,
    });
    if (primary.success === true) {
        const rows = parseTargetsGetResponsePayload(primary.data);
        const match = rows.find((row) => String(row.id) === String(targetId));
        if (match !== undefined) {
            return dataResult(match);
        }
        if (rows.length === 1) {
            return dataResult(rows[0]!);
        }
    }

    const attempts: Array<{url: string; body: Record<string, Uuid>}> = [
        {url: `${intellsysBaseUrl}/sub-system/pluto/api/targets/get`, body: {workspaceId, targetId}},
        {url: `${intellsysBaseUrl}/sub-system/pluto/api/target/get`, body: {workspaceId, id: targetId}},
        {url: `${intellsysBaseUrl}/sub-system/pluto/api/target/get`, body: {workspaceId, targetId}},
        {url: `${intellsysBaseUrl}/sub-system/pluto/api/targets/get-by-id`, body: {workspaceId, id: targetId}},
    ];

    let lastFailure: Result<TargetsGetResponsePayload> | null = primary.success === false ? primary : null;
    for (const {url, body} of attempts) {
        const result = await robustJsonResultClientFetch(
            url,
            {
                method: "POST",
                headers,
                body: JSON.stringify(body),
            },
            zod.custom<TargetsGetResponsePayload>(() => true),
        );
        if (result.success === true) {
            const rows = parseTargetsGetResponsePayload(result.data);
            if (rows.length > 0) {
                const match = rows.find((row) => String(row.id) === String(targetId));
                return dataResult(match ?? rows[0]!);
            }
            return errorResult(new Error("Target payload returned no target records"));
        }
        lastFailure = result;
    }

    return lastFailure ?? errorResult(new Error("Failed to fetch target"));
}

type FilterGroup = {
    logic: "AND" | "OR";
    filters: Array<
        | {
              column: string;
              operator: string;
              value: unknown;
          }
        | FilterGroup
    >;
};

type FilterGroupEntry = {
    column: string;
    operator: string;
    value: unknown;
};

function extractNullSentinelFromInValues(values: Array<string>): {
    nonNullValues: Array<string>;
    hasNullSentinel: boolean;
} {
    const hasNullSentinel = values.includes(NULL_SENTINEL);
    const nonNullValues = values.filter((value) => value !== NULL_SENTINEL);
    return {
        nonNullValues: nonNullValues,
        hasNullSentinel: hasNullSentinel,
    };
}

function buildCreatedUpdatedByNullSentinelApiFilter(resolvedColumnId: string): FilterGroup {
    const equalsOperator = filterOperationToApiOperator(FilterOperation.equals);
    return {
        logic: "OR",
        filters: [
            {
                column: resolvedColumnId,
                operator: equalsOperator,
                value: null,
            },
            {
                column: resolvedColumnId,
                operator: equalsOperator,
                value: SYSTEM_USER_ID,
            },
        ],
    };
}

function buildNullSentinelApiFilter(resolvedColumnId: string): FilterGroupEntry | FilterGroup {
    if (isCreatedUpdatedByFilterColumnId(resolvedColumnId) === true) {
        return buildCreatedUpdatedByNullSentinelApiFilter(resolvedColumnId);
    }
    return {
        column: resolvedColumnId,
        operator: filterOperationToApiOperator(FilterOperation.equals),
        value: null,
    };
}

function convertLineItemToFilterGroupEntry(lineItem: FilterConfigurationLineItem, resolvedColumnId: string): FilterGroupEntry | FilterGroup {
    if (lineItem.operation !== FilterOperation.in) {
        if (lineItem.operation === FilterOperation.equals && lineItem.value === null && isCreatedUpdatedByFilterColumnId(resolvedColumnId) === true) {
            return buildCreatedUpdatedByNullSentinelApiFilter(resolvedColumnId);
        }
        return {
            column: resolvedColumnId,
            operator: filterOperationToApiOperator(lineItem.operation),
            value: coerceFilterLineItemValueForApi(resolvedColumnId, lineItem.value),
        };
    }

    const extractedNullSentinel = extractNullSentinelFromInValues(lineItem.values);

    if (extractedNullSentinel.hasNullSentinel === false) {
        return {
            column: resolvedColumnId,
            operator: filterOperationToApiOperator(FilterOperation.in),
            value: coerceFilterLineItemValueForApi(resolvedColumnId, lineItem.values),
        };
    }

    const nullSentinelFilter = buildNullSentinelApiFilter(resolvedColumnId);

    if (extractedNullSentinel.nonNullValues.length === 0) {
        return nullSentinelFilter;
    }

    return {
        logic: "OR",
        filters: [
            {
                column: resolvedColumnId,
                operator: filterOperationToApiOperator(FilterOperation.in),
                value: coerceFilterLineItemValueForApi(resolvedColumnId, extractedNullSentinel.nonNullValues),
            },
            nullSentinelFilter,
        ],
    };
}

export function convertFilterConfigurationToSrimanFilters(filterConfiguration: FilterConfiguration, searchConfiguration?: SearchConfiguration): FilterGroup {
    let searchData: FilterGroup | null = null;
    if (searchConfiguration !== undefined && (searchConfiguration.searchColumns?.length ?? 0) > 0 && searchConfiguration.searchValue.trim() !== "") {
        const searchValue = searchConfiguration.searchValue;
        const data = searchConfiguration.searchColumns.map((item) => {
            const column = item.startsWith("journey.") === true ? item.slice("journey.".length) : item;
            return {
                column: column,
                operator: filterOperationToApiOperator(FilterOperation.contains),
                value: searchValue,
            };
        });
        searchData = {
            logic: "OR",
            filters: data,
        };
    }

    let otherLineItems = filterConfiguration.lineItems;
    const aonOrFilterGroups: Array<FilterGroup> = [];
    for (const aonConfig of getJourneyAonConfigs()) {
        const partitionResult = partitionAonFilterLineItems(aonConfig, otherLineItems);
        otherLineItems = partitionResult.otherLineItems;
        const aonOrFilterGroup = buildAonCreatedAtOrFilterGroupFromRangesLineItems(aonConfig, partitionResult.aonRangesLineItems);
        if (aonOrFilterGroup !== null) {
            aonOrFilterGroups.push(aonOrFilterGroup);
        }
    }

    const filterData = otherLineItems.map((lineItem) => {
        const columnId =
            lineItem.columnId.startsWith("journey.") === true
                ? lineItem.columnId.slice("journey.".length)
                : lineItem.columnId;
        return convertLineItemToFilterGroupEntry(lineItem, columnId);
    });
    filterData.push(...aonOrFilterGroups);

    // Combine filter data and search data
    const allFilters: FilterGroup["filters"] = [...filterData, ...(searchData !== null ? [searchData] : [])];

    return {
        logic: "AND",
        filters: allFilters,
    };
}

function filterOperationToApiOperator(operation: FilterOperation): string {
    return operation === FilterOperation.contains
        ? "contains"
        : operation === FilterOperation.equals
          ? "equals"
          : operation === FilterOperation.endsWith
            ? "endsWith"
            : operation === FilterOperation.greaterThan
              ? "greaterThan"
              : operation === FilterOperation.greaterThanEqualTo
                ? "greaterThanOrEqualTo"
                : operation === FilterOperation.in
                  ? "in"
                  : operation === FilterOperation.lessThan
                    ? "lessThan"
                    : operation === FilterOperation.lessThanEqualTo
                      ? "lessThanOrEqualTo"
                      : operation === FilterOperation.notContains
                        ? "notContains"
                        : operation === FilterOperation.notEquals
                          ? "notEquals"
                          : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                            operation === FilterOperation.startsWith
                            ? "startsWith"
                            : "?";
}

const EVENT_API_FIXED_COLUMNS: ReadonlySet<string> = new Set([
    "id",
    "createdAt",
    "createdBy",
    "updatedAt",
    "updatedBy",
    "status",
    "templateId",
    "eventContainerId",
    "revisionId",
]);

const EVENT_CONTAINER_API_FIXED_COLUMNS: ReadonlySet<string> = new Set([
    "id",
    "createdAt",
    "createdBy",
    "updatedAt",
    "updatedBy",
    "status",
]);

/**
 * Pluto `/ottopilot/events/event-containers/*` column ids (strict — no aliases):
 * - event fields: bare `columnId` / `columnName`
 * - nested event-container fields: `eventContainer.<columnId>` / `eventContainer.<columnName>`
 *   (see OttopilotEventAdapter.resolveCombinedEventEventContainerColumnReference)
 */
export function resolveEventsFilterColumnId(columnId: string, eventTemplateAdditionalColumnIds: Array<Uuid>, eventContainerAdditionalColumnIds: Array<Uuid>): string {
    if (columnId.startsWith("journey.") === true) {
        return `eventContainer.${columnId.slice("journey.".length)}`;
    }
    if (columnId.startsWith("eventContainer.") === true) {
        return columnId;
    }

    const isAdditionalColumn = uuidZodValidator.safeParse(columnId).success;
    const isEventAdditionalColumn = isAdditionalColumn && eventTemplateAdditionalColumnIds.includes(columnId as Uuid);
    const isEventContainerAdditionalColumn =
        isAdditionalColumn && eventContainerAdditionalColumnIds.includes(columnId as Uuid);

    if (isEventContainerAdditionalColumn) {
        return `eventContainer.${columnId}`;
    }
    if (isEventAdditionalColumn) {
        return columnId;
    }
    return columnId;
}

export function isEventsEventContainersApiColumn(
    columnId: string,
    eventTemplateAdditionalColumnIds: Array<Uuid>,
    eventContainerAdditionalColumnIds: Array<Uuid>,
): boolean {
    const resolved = resolveEventsFilterColumnId(
        columnId,
        eventTemplateAdditionalColumnIds,
        eventContainerAdditionalColumnIds,
    );
    if (resolved.startsWith("eventContainer.") === true) {
        const bare = resolved.slice("eventContainer.".length);
        return (
            EVENT_CONTAINER_API_FIXED_COLUMNS.has(bare) === true ||
            eventContainerAdditionalColumnIds.includes(bare as Uuid)
        );
    }
    return (
        EVENT_API_FIXED_COLUMNS.has(resolved) === true ||
        eventTemplateAdditionalColumnIds.includes(resolved as Uuid)
    );
}

function buildSearchFilterGroup(searchConfiguration?: SearchConfiguration): FilterGroup | null {
    if (searchConfiguration === undefined || (searchConfiguration.searchColumns?.length ?? 0) === 0 || searchConfiguration.searchValue.trim() === "") {
        return null;
    }

    const searchValue = searchConfiguration.searchValue;
    const searchFilters = searchConfiguration.searchColumns.map((columnId) => {
        return {
            column: columnId,
            operator: filterOperationToApiOperator(FilterOperation.contains),
            value: searchValue,
        };
    });

    return {
        logic: "OR",
        filters: searchFilters,
    };
}

function buildEventsSearchFilterGroup(
    searchConfiguration: SearchConfiguration | undefined,
    eventTemplateAdditionalColumnIds: Array<Uuid>,
    eventContainerAdditionalColumnIds: Array<Uuid>,
): FilterGroup | null {
    const searchGroup = buildSearchFilterGroup(searchConfiguration);
    if (searchGroup === null) {
        return null;
    }

    const searchValue = searchConfiguration!.searchValue;
    const filters = searchConfiguration!.searchColumns.flatMap((columnId) => {
        if (
            isEventsEventContainersApiColumn(
                columnId,
                eventTemplateAdditionalColumnIds,
                eventContainerAdditionalColumnIds,
            ) !== true
        ) {
            return [];
        }
        return [
            {
                column: resolveEventsFilterColumnId(columnId, eventTemplateAdditionalColumnIds, eventContainerAdditionalColumnIds),
                operator: filterOperationToApiOperator(FilterOperation.contains),
                value: searchValue,
            },
        ];
    });
    if (filters.length === 0) {
        return null;
    }
    return {
        logic: "OR",
        filters,
    };
}

export function convertFilterConfigurationToSrimanFiltersForEvents(
    filterConfiguration: FilterConfiguration,
    eventTemplateAdditionalColumnIds: Array<Uuid>,
    eventContainerAdditionalColumnIds: Array<Uuid>,
    searchConfiguration?: SearchConfiguration,
): FilterGroup {
    let otherLineItems = filterConfiguration.lineItems;
    const aonOrFilterGroups: Array<FilterGroup> = [];
    const createdAtColumn = resolveEventsFilterColumnId("createdAt", eventTemplateAdditionalColumnIds, eventContainerAdditionalColumnIds);
    for (const aonConfig of getEventAonConfigs()) {
        const partitionResult = partitionAonFilterLineItems(aonConfig, otherLineItems);
        otherLineItems = partitionResult.otherLineItems;
        const scopedAonConfig: AonWorkspaceConfig = {
            ...aonConfig,
            createdAtApiColumn: createdAtColumn,
        };
        const aonOrFilterGroup = buildAonCreatedAtOrFilterGroupFromRangesLineItems(scopedAonConfig, partitionResult.aonRangesLineItems);
        if (aonOrFilterGroup !== null) {
            aonOrFilterGroups.push(aonOrFilterGroup);
        }
    }

    const filterData = otherLineItems.flatMap((lineItem) => {
        if (
            isEventsEventContainersApiColumn(
                lineItem.columnId,
                eventTemplateAdditionalColumnIds,
                eventContainerAdditionalColumnIds,
            ) !== true
        ) {
            return [];
        }
        const column = resolveEventsFilterColumnId(lineItem.columnId, eventTemplateAdditionalColumnIds, eventContainerAdditionalColumnIds);
        return [convertLineItemToFilterGroupEntry(lineItem, column)];
    });
    filterData.push(...aonOrFilterGroups);

    const mappedSearchGroup = buildEventsSearchFilterGroup(searchConfiguration, eventTemplateAdditionalColumnIds, eventContainerAdditionalColumnIds);

    return {
        logic: "AND",
        filters: [...filterData, ...(mappedSearchGroup === null ? [] : [mappedSearchGroup])],
    };
}

export function convertSortConfigurationToSrimanOrderBy(sortConfiguration: SortConfiguration): Array<{
    column: string;
    direction: string;
}> {
    return sortConfiguration.lineItems.map((lineItem) => ({
        column: lineItem.columnId,
        direction: sortDirectionToApiOperator(lineItem.direction),
    }));
}

export function convertSortConfigurationToSrimanOrderByForEvents(
    sortConfiguration: SortConfiguration,
    eventTemplateAdditionalColumnIds: Array<Uuid>,
    eventContainerAdditionalColumnIds: Array<Uuid>,
): Array<{
    column: string;
    direction: string;
}> {
    return sortConfiguration.lineItems.flatMap((lineItem) => {
        if (
            isEventsEventContainersApiColumn(
                lineItem.columnId,
                eventTemplateAdditionalColumnIds,
                eventContainerAdditionalColumnIds,
            ) !== true
        ) {
            return [];
        }
        return [
            {
                column: resolveEventsFilterColumnId(
                    lineItem.columnId,
                    eventTemplateAdditionalColumnIds,
                    eventContainerAdditionalColumnIds,
                ),
                direction: sortDirectionToApiOperator(lineItem.direction),
            },
        ];
    });
}

function sortDirectionToApiOperator(sortDirection: SortDirection): string {
    return sortDirection === SortDirection.ascending
        ? "asc"
        : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          sortDirection === SortDirection.descending
          ? "desc"
          : "?";
}

export async function getTimezones({
    accessToken,
    intellsysBaseUrl,
    timezoneId,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    timezoneId?: Uuid;
}): Promise<Result<Array<TimeZone>>> {
    const timezonesResult = await robustJsonResultServerFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/timezones/get`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                ...(timezoneId !== undefined ? {id: timezoneId} : {}),
            }),
        },
        zod.array(
            zod.object({
                id: uuidZodValidator,
                displayName: zod.string(),
                sdtUtcOffsetInMinutes: integerZodValidator,
                dstUtcOffsetInMinutes: integerZodValidator,
            }),
        ),
    );
    if (timezonesResult.success !== true) {
        return timezonesResult;
    }
    const timezones = timezonesResult.data;

    return dataResult(timezones);
}

// OTP APIs
export async function sendOtp({
    workspaceId,
    phoneNumber,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    phoneNumber: string;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<{id: Uuid}>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/otps/send`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                phoneNumber: phoneNumber,
            }),
        },
        zod.object({id: uuidZodValidator}),
    );
    return result;
}

export async function verifyOtp({
    workspaceId,
    phoneNumber,
    otp,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    phoneNumber: string;
    otp: string;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<{isValid: boolean; otpId: Uuid | null}>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/otps/verify`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                phoneNumber: phoneNumber,
                otp: otp,
            }),
        },
        zod.object({
            isValid: zod.boolean(),
            otpId: uuidZodValidator.nullable(),
        }),
    );
    return result;
}

export async function consumeOtp({
    workspaceId,
    otpId,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    otpId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<{isConsumed: boolean; otpId: Uuid}>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/otps/consume`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
                otpId: otpId,
            }),
        },
        zod.object({
            isConsumed: zod.boolean(),
            otpId: uuidZodValidator,
        }),
    );
    return result;
}

export type ProductInventory = {
    id: Uuid;
    productCatalogueId: Uuid;
    ouId: Uuid;
    inventory: number;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    createdBy: Uuid;
    updatedBy: Uuid;
};

export async function createProductInventory({
    workspaceId,
    productCatalogueId,
    ouId,
    inventory,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    productCatalogueId: Uuid;
    ouId: Uuid;
    inventory?: number;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Uuid>> {
    return robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/product-inventories/create`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId,
                productCatalogueId,
                ouId,
                inventory: inventory ?? 0,
            }),
        },
        uuidZodValidator,
    );
}

export async function getProductInventories({
    workspaceId,
    id,
    productCatalogueId,
    ouId,
    offset,
    limit,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    id?: Uuid | null;
    productCatalogueId?: Uuid | null;
    ouId?: Uuid | null;
    offset?: number;
    limit?: number;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<ProductInventory[]>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/product-inventories/get`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workspaceId,
                id: id ?? null,
                productCatalogueId: productCatalogueId ?? null,
                ouId: ouId ?? null,
                offset: offset ?? 0,
                limit: limit ?? 9999,
            }),
        },
        zod.array(
            zod.object({
                id: uuidZodValidator,
                productCatalogueId: uuidZodValidator,
                ouId: uuidZodValidator,
                inventory: zod.number(),
                createdAt: iso8601DateTimeZodValidator,
                updatedAt: iso8601DateTimeZodValidator,
                createdBy: uuidZodValidator,
                updatedBy: uuidZodValidator,
            }),
        ),
    );
    if (result.success === false) {
        return result;
    }
    return dataResult(result.data);
}

export async function getDuluxBlueStoreAccessibleOrganizationalUnitIds({
    accessToken,
    eventContainerTemplateId,
    intellsysBaseUrl,
    organizationalUnitTemplateId,
    workspaceId,
}: {
    accessToken: string;
    eventContainerTemplateId: Uuid;
    intellsysBaseUrl: string;
    organizationalUnitTemplateId: Uuid;
    workspaceId: Uuid;
}): Promise<Result<Array<Uuid>>> {
    return await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/api/workspace-specific/dulux/blue-store/accessible-organizational-unit-ids/get`,
        {
            body: JSON.stringify({
                eventContainerTemplateId: eventContainerTemplateId,
                organizationalUnitTemplateId: organizationalUnitTemplateId,
            }),
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                "X-Workspace-Id": workspaceId,
            },
            method: "POST",
        },
        zod.array(uuidZodValidator),
    );
}
